import React, { useState, useEffect } from 'react';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { fcmService } from '../../services/fcmService';
import './NotificationPermission.css';

function getInitialPermission() {
  try {
    return typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
  } catch {
    return 'unsupported';
  }
}

export default function NotificationPermission() {
  const [permission, setPermission] = useState(getInitialPermission);
  const [isRequesting, setIsRequesting] = useState(false);
  const [message, setMessage] = useState('');
  const [isDismissed, setIsDismissed] = useState(false);
  const [isBlockedDismissed, setIsBlockedDismissed] = useState(false);
  const [isEnableDismissed, setIsEnableDismissed] = useState(false);
  const [isWarningDismissed, setIsWarningDismissed] = useState(false);
  const [fcmStatus, setFcmStatus] = useState({
    supported: false,
    token: null,
    error: null,
    browserCompatible: false
  });

  // Check FCM status and browser compatibility
  const checkFCMStatus = async () => {
    try {
      // Check browser compatibility
      const browserCompatible = (
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window &&
        window.location.protocol === 'https:'
      );

      if (!browserCompatible) {
        setFcmStatus({
          supported: false,
          token: null,
          error: 'Browser not compatible with push notifications',
          browserCompatible: false
        });
        return;
      }

      // Check if FCM is supported
      const fcmSupported = fcmService.isFCMSupported();
      
      if (!fcmSupported) {
        setFcmStatus({
          supported: false,
          token: null,
          error: 'Firebase Messaging not supported',
          browserCompatible: true
        });
        return;
      }

      // Try to get existing token
      try {
        const token = await fcmService.getCurrentToken();
        
        // If no token and browser permission is granted, this indicates a problem
        if (!token && Notification.permission === 'granted') {
          setFcmStatus({
            supported: true,
            token: null,
            error: 'FCM token generation failed - browser compatibility or IndexedDB issues',
            browserCompatible: true
          });
        } else {
          setFcmStatus({
            supported: true,
            token: token,
            error: null,
            browserCompatible: true
          });
        }
      } catch (error) {
        setFcmStatus({
          supported: true,
          token: null,
          error: error.message || 'FCM token generation failed',
          browserCompatible: true
        });
      }
    } catch (error) {
      setFcmStatus({
        supported: false,
        token: null,
        error: error.message || 'FCM status check failed',
        browserCompatible: false
      });
    }
  };

  useEffect(() => {
    try {
      const currentPermission = typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
      setPermission(currentPermission);
    } catch {
      setPermission('unsupported');
    }
    // Check FCM status
    checkFCMStatus();
    // Check if user has previously dismissed the notification badges
    const enabledDismissed = localStorage.getItem('notificationBadgeDismissed');
    const blockedDismissed = localStorage.getItem('notificationBlockedDismissed');
    const enableDismissed = localStorage.getItem('notificationEnableDismissed');
    const warningDismissed = localStorage.getItem('notificationWarningDismissed');
    
    if (enabledDismissed === 'true') {
      setIsDismissed(true);
    }
    if (blockedDismissed === 'true') {
      setIsBlockedDismissed(true);
    }
    if (enableDismissed === 'true') {
      setIsEnableDismissed(true);
    }
    if (warningDismissed === 'true') {
      setIsWarningDismissed(true);
    }
  }, []);

  const handleRequestPermission = async () => {
    setIsRequesting(true);
    setMessage('');

    try {
      const token = await fcmService.requestPermissionAndGetToken();
      if (token) {
        setMessage('Notifications enabled successfully! You will now receive order updates.');
        setPermission('granted');
        // Reset dismissal states when notifications are newly enabled
        setIsDismissed(false);
        setIsWarningDismissed(false);
        localStorage.removeItem('notificationBadgeDismissed');
        localStorage.removeItem('notificationWarningDismissed');
        // Update FCM status
        await checkFCMStatus();
      } else {
        setMessage('Failed to enable notifications. Please check your browser settings.');
      }
    } catch (error) {
      setMessage('Error enabling notifications. Please try again.');
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem('notificationBadgeDismissed', 'true');
  };

  const handleBlockedDismiss = () => {
    setIsBlockedDismissed(true);
    localStorage.setItem('notificationBlockedDismissed', 'true');
  };

  const handleEnableDismiss = () => {
    setIsEnableDismissed(true);
    localStorage.setItem('notificationEnableDismissed', 'true');
  };

  const handleWarningDismiss = () => {
    setIsWarningDismissed(true);
    localStorage.setItem('notificationWarningDismissed', 'true');
  };

  // Helper component for dismiss button
  const DismissButton = ({ onClick, variant, ariaLabel }) => (
    <Button
      variant={variant}
      size="sm"
      className="notification-dismiss-btn"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
    >
      <i className="fas fa-times" aria-hidden="true"></i>
    </Button>
  );

  // Show FCM error if browser permission is granted but FCM is failing
  if (permission === 'granted' && fcmStatus.error && !isWarningDismissed) {
    return (
      <Alert 
        variant="warning" 
        className="mb-3 notification-alert notification-warning-alert"
        role="alert"
        aria-live="polite"
      >
        <div className="notification-alert-content">
          <div className="notification-alert-header">
            <div className="notification-icon-wrapper notification-icon-warning">
              <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
            </div>
            <div className="notification-alert-text">
              <h6 className="notification-alert-title">Push Notifications Unavailable</h6>
              <p className="notification-alert-description">
                Browser permission granted, but technical issues prevent standard notification storage.
              </p>
            </div>
            <DismissButton
              onClick={handleWarningDismiss}
              variant="outline-warning"
              ariaLabel="Dismiss notification warning"
            />
          </div>
          
          <div className="notification-alert-details">
            <div className="notification-detail-section">
              <strong className="notification-detail-label">Technical Details:</strong>
              <span className="notification-detail-value">{fcmStatus.error}</span>
            </div>
            
            <div className="notification-detail-section">
              <strong className="notification-detail-label">Solution:</strong>
              <span className="notification-detail-value">
                The app is using localStorage as a fallback to store your notification token. 
                Notifications should still work, but you may need to re-enable them if you clear your browser data.
              </span>
            </div>
            
            {!fcmStatus.browserCompatible && (
              <div className="notification-solutions">
                <strong className="notification-detail-label">Try these solutions:</strong>
                <ul className="notification-solutions-list">
                  <li>Update your browser to the latest version</li>
                  <li>Try using Chrome, Firefox, or Edge</li>
                  <li>Disable browser extensions temporarily</li>
                  <li>Ensure you're using HTTPS (not HTTP)</li>
                  <li>Check browser storage permissions in settings</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </Alert>
    );
  }

  // Show success only if both permission is granted AND FCM is working
  if (permission === 'granted' && fcmStatus.token && !isDismissed) {
    return (
      <Alert 
        variant="success" 
        className="mb-3 notification-alert notification-success-alert"
        role="alert"
        aria-live="polite"
      >
        <div className="notification-alert-content">
          <div className="notification-alert-header">
            <div className="notification-icon-wrapper notification-icon-success">
              <i className="fas fa-check-circle" aria-hidden="true"></i>
            </div>
            <div className="notification-alert-text">
              <h6 className="notification-alert-title">Push Notifications Active</h6>
              <p className="notification-alert-description">
                You will receive notifications for order updates, packing status, and delivery information.
              </p>
            </div>
            <DismissButton
              onClick={handleDismiss}
              variant="outline-success"
              ariaLabel="Dismiss success notification"
            />
          </div>
        </div>
      </Alert>
    );
  }

  // Don't render anything if notifications are granted but dismissed
  if (permission === 'granted' && isDismissed) {
    return null;
  }

  if (permission === 'denied' && !isBlockedDismissed) {
    return (
      <Alert 
        variant="warning" 
        className="mb-3 notification-alert notification-blocked-alert"
        role="alert"
        aria-live="polite"
      >
        <div className="notification-alert-content">
          <div className="notification-alert-header">
            <div className="notification-icon-wrapper notification-icon-warning">
              <i className="fas fa-ban" aria-hidden="true"></i>
            </div>
            <div className="notification-alert-text">
              <h6 className="notification-alert-title">Notifications Blocked</h6>
              <p className="notification-alert-description">
                Notifications are currently blocked. Enable them to receive order updates.
              </p>
            </div>
            <DismissButton
              onClick={handleBlockedDismiss}
              variant="outline-warning"
              ariaLabel="Dismiss blocked notification"
            />
          </div>
          
          <div className="notification-alert-details">
            <div className="notification-steps">
              <strong className="notification-detail-label">To enable notifications:</strong>
              <ol className="notification-steps-list">
                <li>Click the lock icon in your browser's address bar</li>
                <li>Select "Allow" for notifications</li>
                <li>Refresh this page</li>
              </ol>
            </div>
          </div>
        </div>
      </Alert>
    );
  }

  if (!isEnableDismissed) {
    return (
      <Alert 
        variant="info" 
        className="mb-3 notification-alert notification-enable-alert"
        role="alert"
        aria-live="polite"
      >
        <div className="notification-alert-content">
          <div className="notification-alert-header">
            <div className="notification-icon-wrapper notification-icon-info">
              <i className="fas fa-bell" aria-hidden="true"></i>
            </div>
            <div className="notification-alert-text">
              <h6 className="notification-alert-title">Enable Notifications</h6>
              <p className="notification-alert-description">
                Get instant updates when your orders are packed and ready for pickup or delivery.
              </p>
            </div>
            <DismissButton
              onClick={handleEnableDismiss}
              variant="outline-info"
              ariaLabel="Dismiss enable notification"
            />
          </div>
          
          <div className="notification-alert-actions">
            <Button 
              variant="primary" 
              size="sm" 
              onClick={handleRequestPermission}
              disabled={isRequesting}
              className="notification-enable-btn"
              aria-label="Enable push notifications"
            >
              {isRequesting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  <span>Enabling...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-bell me-2" aria-hidden="true"></i>
                  <span>Enable Notifications</span>
                </>
              )}
            </Button>
            
            {message && (
              <div 
                className={`notification-message ${message.includes('successfully') ? 'notification-message-success' : 'notification-message-error'}`}
                role="status"
                aria-live="polite"
              >
                {message.includes('successfully') && (
                  <i className="fas fa-check-circle me-2" aria-hidden="true"></i>
                )}
                {message.includes('Error') || message.includes('Failed') ? (
                  <i className="fas fa-exclamation-circle me-2" aria-hidden="true"></i>
                ) : null}
                {message}
              </div>
            )}
          </div>
        </div>
      </Alert>
    );
  }

  // If all notifications are dismissed, don't render anything
  return null;
}
